import { useAtom } from 'jotai';
import { useCallback } from 'react';

import type { LimaSourcesResponseModal } from '@/features/DevicesTable';
import { SourceDialogMode } from '@/features/SourceCreate/SourceCreate.types';
import { useCrudGuard } from '@/pages/Administration/Administration.hooks';
import { selectedCloudSourceAtom } from '@/pages/Administration/Devices/Devices.store';

import { useSourceDialog } from '../../SourceCreate/SourceCreate.hooks';
import type {
  ActionMenuType,
  CloudSourceTreeNode
} from '../CloudSourcesTable.types';
import type { SingleSourceToggleRequest } from './useCloudSourcesActions.types';

/**
 * Custom hook for managing cloud source action menu operations.
 *
 * Handles all action menu operations:
 * - **Edit**: Opens source edit dialog with source type ID
 * - **Delete**: Opens source delete confirmation dialog
 * - **Activate**: Activates a single inactive source
 * - **Deactivate**: Deactivates a single active source
 * - **Resubmit**: Resubmits a failed source for processing
 *
 * All handlers are wrapped with CRUD permission guards to ensure proper authorization.
 *
 * @returns Object containing:
 * - `handleMenuClick` - Action menu click handler that routes to appropriate action
 *
 * @example
 * ```tsx
 * const actions = useCloudSourcesActions();
 * <AgGridReact
 *   context={{ onMenuClick: actions.handleMenuClick }}
 * />
 * ```
 */
export const useCloudSourcesActions = (
  onSingleSourceToggle: (
    toggleState: SingleSourceToggleRequest
  ) => Promise<void>
) => {
  const [, setSelectedCloudSource] = useAtom(selectedCloudSourceAtom);
  const { openSourceDialog, resubmitMutation } = useSourceDialog();

  const editSourceHandler = useCrudGuard((sourceTypeId: unknown) => {
    openSourceDialog(SourceDialogMode.Edit, '', sourceTypeId as number);
  });

  const deleteSourceHandler = useCrudGuard((sourceTypeId: unknown) => {
    openSourceDialog(SourceDialogMode.Delete, '', sourceTypeId as number);
  });

  const resubmitHandler = useCrudGuard((source: unknown) => {
    const typedSource = source as LimaSourcesResponseModal;
    if (!typedSource?.source_uid) return;
    resubmitMutation.mutate(typedSource.source_uid);
  });

  const handleActivateDeactivate = useCallback(
    async (source: LimaSourcesResponseModal, isActive: boolean) => {
      const selectedSrcUids = source?.source_uid;
      if (!selectedSrcUids) return;

      const actionLabel = isActive ? 'Activate' : 'Deactivate';
      const confirmationMsg = `${actionLabel} the selected ${isActive ? 'inactive' : 'active'} source?`;
      await onSingleSourceToggle({
        source_uids: [selectedSrcUids],
        activate: isActive,
        confirmation_msg: confirmationMsg
      });
    },
    [onSingleSourceToggle]
  );

  const handleMenuClick = useCallback(
    (action: ActionMenuType, row: CloudSourceTreeNode) => {
      const selSource =
        row.type === 'provider' ? row.providerData : row.sourceData;

      // Guard: require valid source data
      if (!selSource) return;

      // Set the selected source atom for the dialog to use
      setSelectedCloudSource(selSource);

      switch (action) {
        case 'edit':
          editSourceHandler(selSource.source_type_id);
          break;

        case 'delete':
          deleteSourceHandler(selSource.source_type_id);
          break;

        case 'activate':
          handleActivateDeactivate(selSource, true);
          break;

        case 'deactivate':
          handleActivateDeactivate(selSource, false);
          break;

        case 'resubmit':
          resubmitHandler(selSource);
          break;
      }
    },
    [
      setSelectedCloudSource,
      editSourceHandler,
      deleteSourceHandler,
      handleActivateDeactivate,
      resubmitHandler
    ]
  );

  return {
    handleMenuClick
  };
};
